import { useState, useEffect } from 'react';
import { adminApi } from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationPrevious,
  PaginationNext,
} from '../components/ui/pagination';
import { formatCurrency, formatDateTime } from '../lib/utils';
import { toast } from 'sonner';
import { Search, Loader2, UserCog, Mail, Phone, MapPin } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';

export default function CustomersPage() {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [showDetails, setShowDetails] = useState(false);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCustomers, setTotalCustomers] = useState(0);
  const pageSize = 50;

  useEffect(() => {
    fetchCustomers();
  }, [currentPage]);

  const fetchCustomers = async () => {
    setLoading(true);
    try {
      const params = {
        limit: pageSize,
        skip: (currentPage - 1) * pageSize,
      };
      if (search) params.search = search;
      const res = await adminApi.getCustomers(params);
      setCustomers(res.data);
      // Estimate total
      if (res.data.length === pageSize) {
        setTotalCustomers(currentPage * pageSize + 1);
      } else {
        setTotalCustomers((currentPage - 1) * pageSize + res.data.length);
      }
    } catch (error) {
      toast.error('Failed to load customers');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchCustomers();
  };

  const handleMakeAdmin = async (customer) => {
    if (!window.confirm(`Make ${customer.name} an admin?`)) return;
    try {
      const res = await adminApi.makeCustomerAdmin(customer.id);
      toast.success(`${customer.name} is now an admin. Temp password: ${res.data.temp_password}`);
      fetchCustomers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to make admin');
    }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="customers-page">
      {/* Header */}
      <div>
        <h1 className="font-heading text-3xl text-primary font-semibold">Customers</h1>
        <p className="text-muted-foreground mt-1">Manage your customer base</p>
      </div>

      {/* Search */}
      <Card className="card-base">
        <CardContent className="p-4">
          <form onSubmit={handleSearch} className="flex gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search by name or email..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10 input-base"
                data-testid="search-customers-input"
              />
            </div>
            <Button type="submit" variant="outline" className="border-primary text-primary">
              Search
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Customers Table */}
      <Card className="card-base">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="table-header">
                  <TableHead>Customer</TableHead>
                  <TableHead>Contact</TableHead>
                  <TableHead>Orders</TableHead>
                  <TableHead>Total Spent</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Joined</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {customers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                      No customers found
                    </TableCell>
                  </TableRow>
                ) : (
                  customers.map((customer) => (
                    <TableRow key={customer.id} className="hover:bg-canvas/50">
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                            <span className="text-primary font-semibold">
                              {customer.name?.charAt(0)?.toUpperCase()}
                            </span>
                          </div>
                          <span className="font-medium text-primary">{customer.name}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">
                          <p className="text-muted-foreground">{customer.email}</p>
                          {customer.phone && (
                            <p className="text-muted-foreground text-xs">{customer.phone}</p>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="font-medium">{customer.total_orders}</TableCell>
                      <TableCell className="font-semibold">{formatCurrency(customer.total_spent)}</TableCell>
                      <TableCell>
                        <Badge className={customer.is_admin ? 'bg-primary text-white' : 'badge-pending'}>
                          {customer.is_admin ? 'Admin' : 'Customer'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-muted-foreground text-sm">
                        {formatDateTime(customer.created_at)}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setSelectedCustomer(customer);
                              setShowDetails(true);
                            }}
                          >
                            View
                          </Button>
                          {!customer.is_admin && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleMakeAdmin(customer)}
                              className="text-primary"
                              data-testid={`make-admin-${customer.id}`}
                            >
                              <UserCog className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {!loading && customers.length > 0 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Showing {(currentPage - 1) * pageSize + 1}-
            {Math.min(currentPage * pageSize, totalCustomers)} of ~{totalCustomers}{" "}
            customers
          </p>
          <Pagination>
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    if (currentPage > 1) setCurrentPage(currentPage - 1);
                  }}
                  className={currentPage === 1 ? "pointer-events-none opacity-50" : ""}
                />
              </PaginationItem>
              <PaginationItem>
                <PaginationLink href="#" isActive={true}>
                  {currentPage}
                </PaginationLink>
              </PaginationItem>
              <PaginationItem>
                <PaginationNext
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    if (customers.length === pageSize) setCurrentPage(currentPage + 1);
                  }}
                  className={customers.length < pageSize ? "pointer-events-none opacity-50" : ""}
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        </div>
      )}

      {/* Customer Details Modal */}
      <Dialog open={showDetails} onOpenChange={setShowDetails}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-primary">
              Customer Details
            </DialogTitle>
          </DialogHeader>

          {selectedCustomer && (
            <div className="space-y-4 py-4">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
                  <span className="text-primary font-bold text-2xl">
                    {selectedCustomer.name?.charAt(0)?.toUpperCase()}
                  </span>
                </div>
                <div>
                  <h3 className="font-heading text-lg text-primary">{selectedCustomer.name}</h3>
                  <Badge className={selectedCustomer.is_admin ? 'bg-primary text-white' : 'badge-pending'}>
                    {selectedCustomer.is_admin ? 'Admin' : 'Customer'}
                  </Badge>
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex items-center gap-3 p-3 bg-canvas rounded-lg">
                  <Mail className="w-4 h-4 text-primary" />
                  <span className="text-sm">{selectedCustomer.email}</span>
                </div>
                {selectedCustomer.phone && (
                  <div className="flex items-center gap-3 p-3 bg-canvas rounded-lg">
                    <Phone className="w-4 h-4 text-primary" />
                    <span className="text-sm">{selectedCustomer.phone}</span>
                  </div>
                )}
                {selectedCustomer.address && Object.keys(selectedCustomer.address).length > 0 && (
                  <div className="flex items-start gap-3 p-3 bg-canvas rounded-lg">
                    <MapPin className="w-4 h-4 text-primary mt-0.5" />
                    <span className="text-sm">
                      {selectedCustomer.address.street && `${selectedCustomer.address.street}, `}
                      {selectedCustomer.address.city && `${selectedCustomer.address.city}, `}
                      {selectedCustomer.address.state && `${selectedCustomer.address.state} `}
                      {selectedCustomer.address.pincode}
                    </span>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4 pt-4 border-t">
                <div className="text-center p-4 bg-canvas rounded-lg">
                  <p className="text-2xl font-heading font-bold text-primary">
                    {selectedCustomer.total_orders}
                  </p>
                  <p className="text-sm text-muted-foreground">Total Orders</p>
                </div>
                <div className="text-center p-4 bg-canvas rounded-lg">
                  <p className="text-2xl font-heading font-bold text-accent">
                    {formatCurrency(selectedCustomer.total_spent)}
                  </p>
                  <p className="text-sm text-muted-foreground">Total Spent</p>
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDetails(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
